import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { cn } from 'src/utils/style'
import { Spinner } from 'src/components/spinner'
import { Button } from 'src/components/button'
import type { Property } from 'src/services/properties/models'

interface PropertyStripProps {
  properties: Property[]
  reservationCountByProperty: Record<string, number>
  selectedPropertyId: string | null
  onSelect: (propertyId: string) => void
  isLoading: boolean
  /** Callback ref attached to the trailing sentinel for IntersectionObserver-driven pagination */
  sentinelRef?: (node: HTMLDivElement | null) => void
  /** True while a "next page" fetch is in flight (renders a trailing spinner) */
  isFetchingNext?: boolean
  /** True if there is another page available; controls whether the sentinel is rendered */
  hasMore?: boolean
}

export const PropertyStrip: FC<PropertyStripProps> = ({
  properties,
  reservationCountByProperty,
  selectedPropertyId,
  onSelect,
  isLoading,
  sentinelRef,
  isFetchingNext,
  hasMore,
}) => {
  const { t } = useTranslation()
  const [accordionOpen, setAccordionOpen] = useState(false)

  if (isLoading && properties.length === 0) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner size="sm" />
      </div>
    )
  }

  if (properties.length === 0) {
    return (
      <div className="py-4 text-sm text-text-placeholder">
        {t('reservations.noPropertiesForOrg')}
      </div>
    )
  }

  const selectedProperty = properties.find((r) => r.id === selectedPropertyId) ?? null
  const accordionLabel = selectedProperty
    ? `${t('reservations.propertyAccordionLabel')}: ${
        selectedProperty.name || t('reservations.notSet')
      }`
    : t('reservations.chooseProperty')

  const handleAccordionSelect = (propertyId: string) => {
    onSelect(propertyId)
    setAccordionOpen(false)
  }

  return (
    <>
      {/* Mobile: collapsible accordion */}
      <div className="md:hidden" data-testid="property-strip-mobile">
        <Button
          variant="unstyled"
          type="button"
          aria-expanded={accordionOpen}
          aria-controls="property-accordion-list"
          onClick={() => setAccordionOpen((open) => !open)}
          className="flex w-full items-center justify-between rounded-xl border border-border bg-surface p-4 text-left"
        >
          <span className="font-semibold text-text-primary">{accordionLabel}</span>
          <ChevronDownIcon
            className={cn([
              'h-5 w-5 text-text-placeholder motion-safe:transition-transform',
              accordionOpen && 'rotate-180',
            ])}
            aria-hidden="true"
          />
        </Button>
        {accordionOpen ? (
          <ul
            id="property-accordion-list"
            role="tablist"
            aria-label={t('reservations.propertiesLabel')}
            className="mt-2 flex flex-col gap-2"
          >
            {properties.map((property) => {
              const isActive = property.id === selectedPropertyId
              const count = reservationCountByProperty[property.id] ?? 0
              return (
                <li key={property.id}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => handleAccordionSelect(property.id)}
                    className={cn([
                      'flex w-full flex-col gap-1 rounded-xl border p-4 text-left',
                      isActive
                        ? 'border-primary bg-surface'
                        : 'border-border bg-surface hover:bg-surface-hover',
                    ])}
                  >
                    <span className="font-semibold text-text-primary">
                      {property.name || t('reservations.notSet')}
                    </span>
                    {property.formattedAddress ? (
                      <span className="text-xs text-text-placeholder">
                        {property.formattedAddress}
                      </span>
                    ) : null}
                    <span
                      className={cn([
                        'mt-1 self-start rounded-full px-2 py-0.5 text-xs',
                        isActive ? 'bg-primary text-white' : 'bg-gray-100 text-text-secondary',
                      ])}
                    >
                      {t('reservations.reservationCountOther', { count })}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        ) : null}
      </div>

      {/* Tablet & desktop: horizontal scrollable strip */}
      <div
        className="hidden snap-x snap-mandatory items-stretch gap-3 overflow-x-auto pb-2 md:flex"
        role="tablist"
        aria-label={t('reservations.propertiesLabel')}
        data-testid="property-strip-desktop"
      >
        {properties.map((property) => {
          const isActive = property.id === selectedPropertyId
          const count = reservationCountByProperty[property.id] ?? 0
          return (
            <button
              key={property.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(property.id)}
              className={cn([
                'flex min-w-[240px] shrink-0 snap-start flex-col gap-1 rounded-xl border p-4 text-left transition-colors',
                isActive
                  ? 'border-primary bg-surface'
                  : 'border-border bg-surface hover:bg-surface-hover',
              ])}
            >
              <span className="font-semibold text-text-primary">
                {property.name || t('reservations.notSet')}
              </span>
              {property.formattedAddress ? (
                <span className="text-xs text-text-placeholder">{property.formattedAddress}</span>
              ) : null}
              <span
                className={cn([
                  'mt-1 self-start rounded-full px-2 py-0.5 text-xs',
                  isActive ? 'bg-primary text-white' : 'bg-gray-100 text-text-secondary',
                ])}
              >
                {t('reservations.reservationCountOther', { count })}
              </span>
            </button>
          )
        })}
        {hasMore ? (
          <div
            ref={sentinelRef}
            data-testid="property-strip-sentinel"
            aria-hidden="true"
            className="flex min-w-[60px] shrink-0 items-center justify-center"
          >
            {isFetchingNext ? <Spinner size="sm" /> : null}
          </div>
        ) : null}
      </div>
    </>
  )
}
